import { getRuntimeValue } from './runtime'

interface EnvironmentConfig {
  // API Configuration
  API_BASE_URL: string
  API_TIMEOUT: number

  // Base path for sub-directory IIS deployments
  BASE_PATH: string

  // Mock Configuration
  ENABLE_MOCKS: boolean

  // Authentication
  AUTH_TOKEN_KEY: string
  REFRESH_TOKEN_KEY: string

  // Application
  APP_NAME: string
  APP_VERSION: string

  // Development
  ENABLE_DEVTOOLS: boolean
  LOG_LEVEL: 'debug' | 'info' | 'warn' | 'error'
  DEV_TOKEN?: string

  // Feature Flags
  ENABLE_REAL_TIME: boolean
  ENABLE_NOTIFICATIONS: boolean

  // Environment
  NODE_ENV: 'development' | 'production' | 'test'
  DEV: boolean
  PROD: boolean
}

// Validation functions
// Priority: runtime config (config.json, prod) → import.meta.env (.env.local, dev)
function resolve(key: string): string | undefined {
  const runtimeKey = key.startsWith('VITE_') ? key.slice(5) : key
  return getRuntimeValue(runtimeKey) ?? import.meta.env[key]
}

function getStringEnv(key: string, defaultValue?: string): string {
  const value = resolve(key) || defaultValue
  if (!value) {
    throw new Error(`Environment variable ${key} is required`)
  }
  return value
}

function getBooleanEnv(key: string, defaultValue = false): boolean {
  const value = resolve(key)
  if (value === undefined) return defaultValue
  return value === 'true' || value === '1' || value === 'yes'
}

function getNumberEnv(key: string, defaultValue?: number): number {
  const value = resolve(key)
  if (value === undefined) {
    if (defaultValue !== undefined) return defaultValue
    throw new Error(`Environment variable ${key} is required`)
  }
  const parsed = parseInt(value, 10)
  if (isNaN(parsed)) {
    throw new Error(`Environment variable ${key} must be a valid number`)
  }
  return parsed
}

function getLogLevel(
  key: string,
  defaultValue: 'info' = 'info',
): 'debug' | 'info' | 'warn' | 'error' {
  const value = resolve(key) || defaultValue
  const validLevels = ['debug', 'info', 'warn', 'error']
  if (!validLevels.includes(value)) {
    console.warn(`Invalid log level "${value}", using "${defaultValue}"`)
    return defaultValue
  }
  return value as 'debug' | 'info' | 'warn' | 'error'
}

// Getters ensure each property is resolved lazily, AFTER loadRuntimeConfig() has populated
// the runtime config. Static evaluation at module-import time would read an empty config.
export const env: EnvironmentConfig = {
  // API Configuration
  get API_BASE_URL() { return getStringEnv('VITE_API_BASE_URL') },
  get API_TIMEOUT() { return getNumberEnv('VITE_API_TIMEOUT', 30000) },

  // Base path — reads BASE_PATH from config.json (runtime) or VITE_BASE_PATH from .env (dev)
  get BASE_PATH() { return getStringEnv('VITE_BASE_PATH', '/') },

  // Mock Configuration
  get ENABLE_MOCKS() { return getBooleanEnv('VITE_ENABLE_MOCKS', false) },

  // Authentication
  get AUTH_TOKEN_KEY() { return getStringEnv('VITE_AUTH_TOKEN_KEY', 'auth_token') },
  get REFRESH_TOKEN_KEY() { return getStringEnv('VITE_REFRESH_TOKEN_KEY', 'refresh_token') },

  // Application
  get APP_NAME() { return getStringEnv('VITE_APP_NAME', 'Yubiq FEA UI') },
  get APP_VERSION() { return getStringEnv('VITE_APP_VERSION', '1.0.0') },

  // Development
  get ENABLE_DEVTOOLS() { return getBooleanEnv('VITE_ENABLE_DEVTOOLS', true) },
  get LOG_LEVEL() { return getLogLevel('VITE_LOG_LEVEL', 'info') },
  DEV_TOKEN: import.meta.env.VITE_DEV_TOKEN,

  // Feature Flags
  get ENABLE_REAL_TIME() { return getBooleanEnv('VITE_ENABLE_REAL_TIME', true) },
  get ENABLE_NOTIFICATIONS() { return getBooleanEnv('VITE_ENABLE_NOTIFICATIONS', true) },

  // Environment — these come from Vite build-time constants, not runtime config
  NODE_ENV: (import.meta.env.NODE_ENV as 'development' | 'production' | 'test') || 'development',
  DEV: import.meta.env.DEV || false,
  PROD: import.meta.env.PROD || false,
}

// Called explicitly from main.ts after loadRuntimeConfig() completes
export function validateEnvironment(): void {
  try {
    new URL(env.API_BASE_URL)
  } catch {
    throw new Error(`Invalid API_BASE_URL: ${env.API_BASE_URL}`)
  }

  if (env.API_TIMEOUT < 1000 || env.API_TIMEOUT > 120000) {
    console.warn(`API timeout ${env.API_TIMEOUT}ms is outside recommended range (1000-120000ms)`)
  }

  if (env.DEV) {
    if (env.ENABLE_MOCKS) console.log('🎭 Mock Service Worker is enabled')
    if (env.ENABLE_DEVTOOLS) console.log('🛠️ Development tools are enabled')
  }
}

// Utility functions
export function isProduction(): boolean {
  return env.NODE_ENV === 'production'
}

export function isDevelopment(): boolean {
  return env.NODE_ENV === 'development'
}

export function isTest(): boolean {
  return env.NODE_ENV === 'test'
}

export function shouldEnableMocks(): boolean {
  const isBrowser = typeof window !== 'undefined'
  return env.ENABLE_MOCKS && isBrowser && (isDevelopment() || isTest())
}

export function getApiUrl(path = ''): string {
  const baseUrl = env.API_BASE_URL.endsWith('/') ? env.API_BASE_URL.slice(0, -1) : env.API_BASE_URL
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${baseUrl}${cleanPath}`
}

// Export environment object as default
export default env
